import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ChildInfo } from '../utils';
import { NumberStepper } from './NumberStepper';
import { AgeSelector } from './AgeSelector';

export function NumberOfGuestsSelector(props: {
  value: {
    adults: number;
    children: number;
    childrenInfo: ChildInfo[];
  };
  onChange: (value: {
    adults: number;
    children: number;
    childrenInfo: ChildInfo[];
  }) => void;
}) {
  const { t } = useTranslation();

  const handleChildrenChange = (children: number) => {
    const newChildrenInfo = props.value.childrenInfo.concat(
      Array(children).fill({
        age: 0,
        additionalBed: false,
      }),
    );
    props.onChange({
      adults: props.value.adults,
      children,
      childrenInfo: newChildrenInfo.slice(0, children),
    });
  };

  return (
    <Box>
      <Box sx={{ py: 1 }}>
        <Stack direction="row" spacing={3}>
          <Box>
            <Typography sx={{ mb: 1 }}>
              {t('numberOfGuestsSelector.adultsSelector')}
            </Typography>
            <Box sx={{ display: 'flex' }}>
              <NumberStepper
                value={props.value.adults}
                onChange={(adults) =>
                  props.onChange({
                    adults,
                    children: props.value.children,
                    childrenInfo: props.value.childrenInfo,
                  })
                }
                max={6}
                min={0}
              />
            </Box>
          </Box>
          <Box>
            <Typography sx={{ mb: 1 }}>
              {t('numberOfGuestsSelector.childrenSelector')}
            </Typography>
            <Box sx={{ display: 'flex' }}>
              <NumberStepper
                value={props.value.children}
                onChange={(children) => handleChildrenChange(children)}
                max={4}
                min={0}
              />
            </Box>
          </Box>
        </Stack>
      </Box>

      {props.value.children > 0 && (
        <Box sx={{ py: 1 }}>
          <Stack spacing={2}>
            <Typography>
              {t('numberOfGuestsSelector.childrenAges', {
                count: props.value.children,
              })}
            </Typography>
            <Stack direction="row" spacing={2}>
              {props.value.childrenInfo.map((ci, i) => (
                <Box key={`child-${ci.age}-${i}`}>
                  <AgeSelector
                    value={ci.age}
                    onChange={(value) => {
                      const newChildrenInfo = props.value.childrenInfo.slice();
                      newChildrenInfo[i].age = value;
                      newChildrenInfo[i].additionalBed =
                        value > 7 ? true : false;
                      props.onChange({
                        adults: props.value.adults,
                        children: props.value.children,
                        childrenInfo: newChildrenInfo,
                      });
                    }}
                  />
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={ci.additionalBed}
                          disabled={ci.age > 7}
                          onChange={(e, value) => {
                            const newChildrenInfo =
                              props.value.childrenInfo.slice();
                            newChildrenInfo[i].additionalBed = value;
                            props.onChange({
                              adults: props.value.adults,
                              children: props.value.children,
                              childrenInfo: newChildrenInfo,
                            });
                          }}
                        />
                      }
                      label={t('numberOfGuestsSelector.additionalBed')}
                    />
                  </FormGroup>
                </Box>
              ))}
            </Stack>
          </Stack>
        </Box>
      )}
    </Box>
  );
}
